import React, { useMemo, useEffect, useState, useRef } from 'react'
import ListenButton from '@/components/atoms/ListenButton/ListenButton'
import RecordingBox from '@/components/atoms/RecordingBox/RecordingBox'
import DraggableWord from '@/components/interAction/Drag/DragableWord'
import GradingState from "@/components/organisms/Grading/GradingState";
import { Button } from '@/components/atoms'
import { academyLearningScheduleApi } from 'services/api';
import { Dialog } from '@/components/organisms/Modal/Dialog';
import RecordingGradingContents from '@/components/Modal/RecordingGradingContents';
import { calculateTextAccuracy } from './utils/textAccuracy';

interface GradingWordChoiceProps {
  type?: 'scoreCard' | 'gradingCard'
  activity?: any
  item?: any
  responseJson?: string
  recordingGradingResult?: 'correct' | 'incorrect'
  onRecordingGradingChange?: (status: 'correct' | 'incorrect') => void
  manualGradingResult?: 'correct' | 'incorrect' | 'grading'
  onManualGradingChange?: (status: 'correct' | 'incorrect' | 'grading' | undefined) => void
  unitType?: string
  canUseAi?: boolean
  manualLocked?: boolean
  recordingLocked?: boolean
}
const GradingWordChoice = ({ type = 'scoreCard', activity, item, responseJson, recordingGradingResult, onRecordingGradingChange, manualGradingResult, onManualGradingChange, unitType, canUseAi = true, manualLocked = false, recordingLocked = false }: GradingWordChoiceProps) => {
  // 단어 고르기 테두리 색상 결정 함수 - 개별 상태별로
  const getWordChoiceBorderColor = (status: 'correct' | 'word_incorrect' | 'recording_incorrect' | 'both_incorrect' | 'grading') => {
    switch (status) {
        case 'correct':
        return 'border-green-500';
        case 'word_incorrect':
        case 'recording_incorrect':
        case 'both_incorrect':
        return 'border-red-500';
        case 'grading':
        return 'border-gray-200';
        default:
        return 'border-gray-200';
    }
  };

  // 실제 데이터를 파싱하여 사용
  const { correctAnswer, imageUrl, selectedWords, userAnswer, recordingUrl, writingStatus, titleTranslation, audioSrc } = useMemo(() => {
    if (!activity || !activity.items || activity.items.length === 0) {
      // 더미 데이터
      return {
        correctAnswer: 'a book on the desk',
        imageUrl: null,
        selectedWords: [] as string[],
        userAnswer: '',
        recordingUrl: null as string | null,
        writingStatus: 'grading' as const,
        titleTranslation: null as string | null,
        audioSrc: null as string | null
      };
    }

    // responseJson 파싱 (단일 객체 또는 맵 둘 다 지원)
    let parsed: any = {};
    if (responseJson) {
      try {
        parsed = JSON.parse(responseJson);
      } catch (e) {
        console.error('responseJson 파싱 실패:', e);
      }
    }
    const isMapLike = parsed && typeof parsed === 'object' && Object.keys(parsed).some(k => /^\d+$/.test(k));
    const activityResponse: any = isMapLike ? (parsed['0'] ?? Object.values(parsed)[0] ?? {}) : parsed;
    const firstItem = item || activity.items[0];
    let contentJson: any = {};
    try {
      contentJson = JSON.parse(firstItem.contentJson || '{}');
    } catch (e) {
      console.error('contentJson 파싱 실패:', e);
    }

    // configJson에서 titleTranslation 가져오기
    let configJson: any = {};
    let titleTranslation: string | null = null;
    try {
      configJson = JSON.parse(activity.configJson || '{}');
      // 문항별 titleTranslation 우선 (contentJson), 없으면 설정값(configJson) 사용
      titleTranslation = contentJson.titleTranslation || configJson.titleTranslation || null;
    } catch (e) {
      console.error('configJson 파싱 실패:', e);
    }

    // 학생 응답 가져오기 (selectedWords 또는 answer 문자열)
    const itemResponse: any = activityResponse || {};
    const selectedWords: string[] = Array.isArray(itemResponse.selectedWords) ? itemResponse.selectedWords : [];
    const userAnswer: string = typeof itemResponse.answer === 'string'
      ? itemResponse.answer
      : (Array.isArray(selectedWords) ? selectedWords.join(' ') : '');

    // 정답 가져오기
    let answerJson: any = {};
    try {
      answerJson = JSON.parse(firstItem.answerJson || '{}');
    } catch (e) {
      console.error('answerJson 파싱 실패:', e);
    }

    const isCorrectFlag: boolean | undefined = typeof itemResponse.isCorrect === 'boolean' ? itemResponse.isCorrect : undefined;
    const normalize = (s: string) => s.replace(/[^a-z0-9\s]/gi, '').trim().toLowerCase();
    // 정답 텍스트 미리 계산 (변수 초기화 이전 참조 방지)
    const correctText = (answerJson.correctSentence || contentJson.sentence || contentJson.word || '').toString();
    const computedIsCorrect = ((): boolean | null => {
      if (!userAnswer) return null;
      const ua = normalize(userAnswer);
      const ca = normalize(correctText);
      if (ua && ca) return ua === ca;
      return null;
    })();
    const writingStatus: 'correct' | 'word_incorrect' | 'recording_incorrect' | 'both_incorrect' | 'grading' =
      userAnswer
        ? (isCorrectFlag !== undefined
            ? (isCorrectFlag ? 'correct' : 'word_incorrect')
            : (computedIsCorrect === null ? 'grading' : (computedIsCorrect ? 'correct' : 'word_incorrect')))
        : 'grading';
    const recordingUrl: string | null = typeof itemResponse.recordingUrl === 'string' ? itemResponse.recordingUrl : null;

    return {
      correctAnswer: correctText || 'a book on the desk',
      imageUrl: contentJson.imageUrl || contentJson.wordImageUrl || null,
      selectedWords,
      userAnswer,
      recordingUrl,
      writingStatus,
      titleTranslation,
      audioSrc: contentJson.audioUrl || contentJson.wordAudioUrl || null
    };
  }, [activity, responseJson, item]);

  // 단어 고르기 결과 렌더링 함수 (선택한 단어 순서대로 토큰 표시)
  const renderWordChoiceResult = () => {
    const tokens = (selectedWords && selectedWords.length > 0)
      ? selectedWords
      : (userAnswer ? userAnswer.split(/\s+/).filter(Boolean) : []);
    if (tokens.length === 0) {
      return <span className='text-gray-500'>학생 입력이 없습니다.</span>;
    }

    const normalize = (s: string) => s.replace(/[^a-z0-9\s]/gi, '').trim().toLowerCase();
    const correctTokens = correctAnswer ? correctAnswer.split(/\s+/).filter(Boolean) : [];

    return tokens.map((word: string, index: number) => {
      const isCorrect = index < correctTokens.length && normalize(word) === normalize(correctTokens[index]);
      const color = isCorrect ? '#1D9F73' : '#F95567';
      
      return (
        <DraggableWord
          key={`${word}-${index}`}
          id={word}
          color={color}
          readOnly={true}
        />
      );
    });
  };

  // 정답 문장에서 titleTranslation 부분을 강조하여 렌더링
  const renderCorrectAnswerWithHighlight = () => {
    if (unitType === 'STARTER_STRUCTURE' && titleTranslation) {
      const lowerFull = correctAnswer.toLowerCase();
      const lowerTarget = titleTranslation.toLowerCase();
      const index = lowerFull.indexOf(lowerTarget);
      
      if (index !== -1) {
         const before = correctAnswer.slice(0, index);
         const target = correctAnswer.slice(index, index + titleTranslation.length);
         const after = correctAnswer.slice(index + titleTranslation.length);
         
         return (
           <span>
               {before}
               <span className="underline decoration-2 underline-offset-4 decoration-indigo-500">{target}</span>
               {after}
           </span>
         );
      }
   }
    return <span>{correctAnswer}</span>;
  };

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [currentDialogStatus, setCurrentDialogStatus] = useState<'correct' | 'incorrect'>('correct');
  const [aiLoading, setAiLoading] = useState(false);
  const canGradeRecording = type === 'gradingCard' && !recordingLocked;
  const showRecordingViewButton = type === 'scoreCard' || recordingLocked;
  const dialogReadOnly = type !== 'gradingCard' || recordingLocked;

  const handleOpenDialog = () => {
    const current: 'correct' | 'incorrect' = recordingGradingResult || 'correct';
    console.log('WordChoice 다이얼로그 열기:', { activity: activity?.id, recordingGradingResult, current });
    setCurrentDialogStatus(current);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
  };

  const handleConfirmGrading = () => {
    console.log('WordChoice 채점 확인:', { activity: activity?.id, currentDialogStatus });
    if (onRecordingGradingChange) {
      onRecordingGradingChange(currentDialogStatus);
    }
    handleCloseDialog();
  };

  const handleAIRecordingGrading = async () => {
    if (!canGradeRecording) return;
    if (!canUseAi) {
      alert('AI 채점은 플러스 요금제 전용입니다.');
      return;
    }
    try {
      // 녹음이 없으면 백엔드에서 오답 처리하므로 그대로 요청
      const scheduleId = (window as any).__CURRENT_SCHEDULE_ID__;
      setAiLoading(true);
      onRecordingGradingChange?.(undefined as any);
      const res = await academyLearningScheduleApi.aiGradeDictation(scheduleId, {
        recordingUrl: recordingUrl || '',
        correctWord: correctAnswer,
      });
      const ok = !!res?.data?.correct;
      onRecordingGradingChange?.(ok ? 'correct' : 'incorrect');
    } catch (e) {
      console.error('WORD_CHOICE AI 채점 실패', e);
      alert('AI 채점에 실패했습니다.');
    } finally {
      setAiLoading(false);
    }
  };

  const buildSentenceWithStudentPart = (baseSentence: string, targetPart: string | null, studentPart: string) => {
    if (!baseSentence || !targetPart) return null;
    const lowerBase = baseSentence.toLowerCase();
    const lowerTarget = targetPart.toLowerCase();
    const index = lowerBase.indexOf(lowerTarget);
    if (index === -1) return null;
    const before = baseSentence.slice(0, index);
    const after = baseSentence.slice(index + targetPart.length);
    return `${before}${studentPart}${after}`;
  };

  const studentText = useMemo(() => {
    if (userAnswer && userAnswer.trim().length > 0) return userAnswer.trim();
    if (selectedWords && selectedWords.length > 0) return selectedWords.join(' ').trim();
    return '';
  }, [userAnswer, selectedWords]);

  const accuracyPercent = useMemo(() => {
    if (!correctAnswer) return null;
    if (unitType === 'STARTER_STRUCTURE') {
      const mergedSentence = buildSentenceWithStudentPart(correctAnswer, titleTranslation, studentText);
      if (mergedSentence) {
        return calculateTextAccuracy(correctAnswer, mergedSentence);
      }
    }
    if (!studentText) return null;
    return calculateTextAccuracy(correctAnswer, studentText);
  }, [correctAnswer, studentText, unitType, titleTranslation]);

  const manualResultNormalized: 'correct' | 'incorrect' | null =
    manualGradingResult === 'correct' ? 'correct'
      : manualGradingResult === 'incorrect' ? 'incorrect'
      : null;
  const lastEmittedStatusRef = useRef<'correct' | 'incorrect' | null>(null);

  // 다른 문제/응답으로 전환될 때 중복 방지용 캐시 초기화
  useEffect(() => {
    lastEmittedStatusRef.current = null;
  }, [activity?.id, item?.id, responseJson, type]);

  const autoWritingStatus: 'correct' | 'word_incorrect' | 'recording_incorrect' | 'both_incorrect' | 'grading' =
    writingStatus;

  // 내부 상태 대신 prop 사용 (있으면 prop, 없으면 내부 상태)
  // 자동 채점 결과를 바로 부모에 전달 (중복 전송 방지)
  useEffect(() => {
    if (type !== 'gradingCard') return;
    if (!onManualGradingChange) return;
    if (manualLocked) return;

    if (manualGradingResult && manualGradingResult !== 'grading') return;
    if (autoWritingStatus === 'grading') return;
    const target = autoWritingStatus === 'correct' ? 'correct' : 'incorrect';
    if (manualResultNormalized === target) return;
    if (lastEmittedStatusRef.current === target) return;
    lastEmittedStatusRef.current = target;
    onManualGradingChange(target);
  }, [type, autoWritingStatus, manualResultNormalized, manualGradingResult, onManualGradingChange, manualLocked]);
    
  const displayWritingStatus: 'correct' | 'word_incorrect' | 'recording_incorrect' | 'both_incorrect' | 'grading' =
    manualGradingResult && manualGradingResult !== 'grading'
      ? (manualGradingResult === 'correct' ? 'correct' : 'word_incorrect')
      : autoWritingStatus;

  return (
    <div>
        <div className='flex flex-col gap-2'>
            <p className='text-lg font-bold'>
              {unitType === 'TRANSLATION' 
                ? '단어 고르기' 
                : unitType === 'STARTER_STRUCTURE' 
                  ? '부분 영작' 
                  : '단어를 고르고 녹음도 해보세요'}
            </p>
            {/* 단어 고르기 , 부분영작 같이 사용 */}
            <div className='relative p-3 rounded-2xl border border-gray-100'>
                {imageUrl ? (
                  <img src={imageUrl} alt=""  className='rounded-2xl'/>
                ) : (
                  <div className="flex items-center justify-center w-full h-48 bg-gray-100 rounded-2xl text-gray-400 font-bold text-sm">
                    이미지가 없습니다
                  </div>
                )}
                {type !== 'gradingCard' && (
                  <ListenButton size={40} className='absolute top-2 right-2 !p-1' audioSrc={audioSrc || undefined} />
                )}
            </div>
            {/* 학습에서만 녹음바 노출, 채점에서는 숨김 (별도 버튼 제거) */}
            <div className='relative'>
                {type !== 'gradingCard' && (
                  <RecordingBox className='!my-0' initialStep={3} />
                )}
            </div>

            <div>
                <p className='text-sm font-bold text-gray-500'>정답</p>
                <div className='flex justify-center items-center p-5 text-2xl font-bold bg-gray-50 rounded-2xl'>
                {renderCorrectAnswerWithHighlight()}
                </div>
            </div>

            {/* 녹음한 답 상태 (녹음이 있는 경우에만) */}
            {recordingUrl && (
              <div className='flex justify-between items-center py-3 px-5 bg-gray-50 rounded-2xl border border-gray-200'>
                  <p className='text-sm font-bold'>녹음한 답</p>
                  <div className='flex gap-2'>
                      {canGradeRecording && (
                        <>
                          {canUseAi && (
                            <Button size='small' onClick={handleAIRecordingGrading} disabled={aiLoading}>
                              {aiLoading ? '채점중...' : 'AI 채점'}
                            </Button>
                          )}
                          <Button size='small' intent='secondary' onClick={handleOpenDialog}>녹음 듣고 채점하기</Button>
                        </>
                      )}
                      {showRecordingViewButton && (
                          <Button size='small' intent='secondary' onClick={handleOpenDialog}>녹음 듣기</Button>
                      )}
                      <div className='w-[70px] flex-none flex items-center justify-center'>
                          <GradingState status={
                            recordingGradingResult
                              ? (recordingGradingResult === 'correct' ? 'correct' : 'recording_incorrect')
                              : 'grading'
                          } />
                      </div>
                  </div>
              </div>
            )}

            {/* 입력한 답 */}
            <div className={`rounded-2xl border overflow-hidden ${getWordChoiceBorderColor(displayWritingStatus)}`}>
                <div className='flex justify-between items-center px-4 py-2'>
                    <p className='text-sm font-bold text-gray-500'>입력한 답</p>
                    <div className='flex items-center gap-3'>
                      <GradingState status={displayWritingStatus} />
                    </div>
                </div>
                <div className='flex justify-center items-center p-5 text-2xl font-bold bg-gray-50'>
                    {unitType === 'STARTER_STRUCTURE' && titleTranslation ? (
                       <div className='text-center'>
                          {(() => {
                              const lowerFull = correctAnswer.toLowerCase();
                              const lowerTarget = titleTranslation.toLowerCase();
                              const index = lowerFull.indexOf(lowerTarget);
                              
                              if (index !== -1) {
                                 const before = correctAnswer.slice(0, index);
                                 const after = correctAnswer.slice(index + titleTranslation.length);
                                 const isCorrect = displayWritingStatus === 'correct';
                                 const isGrading = displayWritingStatus === 'grading';
                                 // 정답 여부에 따른 색상 적용 (기존 칩 색상과 유사하게: Green/Red, 채점중/미입력은 검정/회색)
                                 const textColor = isCorrect ? 'text-[#1D9F73]' : (isGrading ? 'text-gray-700' : 'text-[#F95567]');
                                 
                                 return (
                                   <span>
                                       {before}
                                       <span className={`${textColor} underline decoration-2 underline-offset-4`}>
                                           {studentText || '(입력없음)'}
                                       </span>
                                       {after}
                                   </span>
                                 );
                              }
                              return (
                               <div className='flex flex-wrap gap-3 justify-center px-1'>
                                   {renderWordChoiceResult()}
                               </div>
                              );
                          })()}
                       </div>
                   ) : (
                    <div className='flex flex-wrap gap-3 justify-center px-1'>
                      {renderWordChoiceResult()}
                    </div>
                   )}
                </div>
            </div>

            {unitType === 'STARTER_STRUCTURE' && accuracyPercent !== null && (
              <div className='p-4 bg-gray-50 rounded-2xl border border-gray-100'>
                <p className='text-sm text-gray-500'>영작 정답 근접도</p>
                <p className='text-2xl font-bold text-indigo-600'>{accuracyPercent}%</p>
              </div>
            )}
        </div>
        <Dialog
                open={isDialogOpen}
                onCancel={handleCloseDialog}
                onOk={dialogReadOnly ? handleCloseDialog : handleConfirmGrading}
                okText={dialogReadOnly ? '닫기' : '확인'}
                cancelText={dialogReadOnly ? undefined : '취소'}
            >
                <RecordingGradingContents 
                  word={correctAnswer}
                  recordingUrl={recordingUrl || undefined}
                  initialGradingStatus={currentDialogStatus}
                  onGradingChange={dialogReadOnly ? undefined : setCurrentDialogStatus}
                  readOnly={dialogReadOnly}
                />
            </Dialog>
    </div>
  )
}

export default GradingWordChoice